import { HttpStatus } from "../../commons";
import { PaginationPayload } from "../../commons/types/pagination.type";
import { UserDetailsResponsePayload } from "../settings/setting_types";

export interface TotalPlacedOrderResponsePayload {
	totalPlacedOrders: number;
}

export interface TotalOrdersWithinDateRangeResponsePayload {
	noOfOrders: number;
}

export type TotalPlacedOrderWithDatePayload = {
	startDate: string;
	endDate: string;
};

export interface TotalPlacedOrderState {
	status: HttpStatus;
	message?: string;
	pendingOrders: TotalPlacedOrderResponsePayload;
	totalOrdersWithinDateRange: number;
}

export interface OrderProductResponsePayload {
	productId: string;
	quantity: number;
	price: number;
	amount: number;
	vendorId: string;
	image: string;
	name: string;
	addedAt: string;
	description?: string;
}

interface LocationT {
	type: string;
	coordinates: number[];
}

export interface OrdersResponsePayload {
	buyerId: string;
	vendorId: string;
	amount: {
		deliveryFee: number;
		unit: string;
		subtotal: number;
		total: number;
		vat: number;
		packaging: number;
		serviceCharge: number;
	};
	totalItemInCart: number;
	status: OrderStatus;
	location: LocationT;
	deliverTo: {
		state: string;
		city: string;
		address: string;
		addressId: string;
		location: LocationT;
	};
	paymentInfo: {
		paymentMethod: string;
		paymentStatus: string;
		paymentReference: string;
	};
	updatedAt: string;
	addressId: string;
	orderId: string;
	vendor: UserDetailsResponsePayload;
	cartType: string;
	commission?: {
		vendorCommission: number;
		commissionFromRider: number;
		commissionFromVendor: number;
		riderCommission: number;
		fleetOwnerCommission: number;
	};
	products: Array<OrderProductResponsePayload>;
	addedAt: string;
}

export interface GetOrdersResponsePayload {
	orders: Array<Order>;
	paginationCursor: paginationCursorResponsePayload;
}

export interface paginationCursorResponsePayload {
	before: string;
	after: string;
	hasNext: boolean;
	hasPrevious: boolean;
}

export interface GetOrderState extends GetOrdersResponsePayload {
	status: HttpStatus;
	message?: string;
}

export interface TotalOrderPerDayResponsePayload {
	sunday: number;
	monday: number;
	tuesday: number;
	wednesday: number;
	thursday: number;
	friday: number;
	saturday: number;
}

export type GetTotalOrderPerDayRequestPayload = {
	id: number;
	day: string;
	noOfOrder: number;
};

export interface TotalOrderPerDayState {
	status: HttpStatus;
	message?: string;
	response: TotalOrderPerDayResponsePayload;
}

export interface TotalOrderPerDayPayload {
	startDate: string;
	endDate: string;
}

export interface OngoingOrderResponsePayload {
	orders: OrdersResponsePayload[];
	paginationCursor: paginationCursorResponsePayload;
}

export interface OngoingOrderState extends OngoingOrderResponsePayload {
	status: HttpStatus;
	message?: string;
}

export interface ConfirmOrderResponsePayload {
	confirmedOrder: true;
}

export interface ConfirmOrderPayload {
	orderId: string;
}

export interface ConfirmOrderState {
	status: HttpStatus;
	message?: string;
	response?: ConfirmOrderResponsePayload;
}

export interface DeclineOrderResponsePayload {
	declinedOrder: true;
}

export type DeclineOrderPayload = {
	orderId: string;
	reason: string;
};

export interface DeclineOrderState {
	status: HttpStatus;
	message?: string;
	response?: DeclineOrderResponsePayload;
}

export interface TotalNumberOfActiveOrderResponsePayload {
	totalOrderValue: number;
}

export interface NumberOfActiveOrderState {
	status: HttpStatus;
	message?: string;
	response?: TotalNumberOfActiveOrderResponsePayload;
}

export interface GetOrderDetailsResponsePayload
	extends Omit<OrdersResponsePayload, "orderId"> {
	_id: string;
	vendorAddress: string;
}

export type Location = {
	longitude: number;
	latitude: number;
};

export type Amount = {
	deliveryFee: number;
	unit: string;
	subtotal: number;
	total: number;
	vat: number;
	packaging: number;
	serviceCharge: number;
};

export type DeliverTo = {
	address: string;
	addressId: string;
	state: string | null;
	city: string | null;
	location: Location;
};

export type PaymentInfo = {
	paymentMethod: string;
	paymentStatus: string;
	paymentReference: string;
};

export type Product = {
	quantity: number;
	price: number;
	amount: number;
	packaging: number;
	image: string;
	name: string;
	addedAt: string;
	productId: string;
	vendorId: string;
	cartId: string;
};

export type LogMeta = {
	userId?: string;
	vendorId?: string;
	adminId?: string;
	riderId?: string;
	reason?: string;
};

export type Log = {
	changedAt: string;
	state: string;
	by: string;
	meta: LogMeta;
};

export type UnderPaid = {
	balance: number;
	text: string;
};

export type Vendor = {
	name: string;
	logo: string;
	vendorType: string;
	address: string;
	location: Location;
	vendorId: string;
};

export type Commission = {
	commissionFromVendor: number;
	commissionFromRider: number;
	riderCommission: number;
	vendorCommission: number;
	fleetOwnerCommission: number;
};

export type Order = {
	buyerId: string;
	vendorId: string;
	amount: Amount;
	totalItemInCart: number;
	status: string;
	location: Location;
	deliverTo: DeliverTo;
	paymentInfo: PaymentInfo;
	addedAt: string;
	updatedAt: string;
	products: Product[];
	logs: Log[];
	underPaid: UnderPaid;
	commissionPercentage: number;
	orderId: string;
	vendor: Vendor;
	timelines: unknown[]; // Replace with proper type if known
	commission: Commission;
	pickupCode: string;
};

export interface GetOrderItemDetailsResponsePayload {
	order: Order;
}

export interface GetOrderDetailsState {
	status: HttpStatus;
	message?: string;
	order?: OrdersResponsePayload;
}

export interface DeclineOrderFormInput {
	reason: string;
}

export interface GetTotalOrderResponsePayload {
	noOfOrders: number;
}

export interface GetTotalOrderResponsePayloadInitialState {
	status: HttpStatus;
	message?: string;
	response: GetTotalOrderResponsePayload;
}

export enum OrderStatus {
	placed = "placed", // pending orders
	ongoing = "ongoing", // ongoing orders
	ready = "ready", // ready orders
	confirmed = "confirmed",
	dispatched = "dispatched",
	completed = "completed",
	canceled = "canceled",
	declined = "declined",
	delivered = "delivered",
	assigned = "assigned",
	ready_for_pick_up = "ready_for_pick_up",
}

export enum OrderTabs {
	pending = "pending",
	confirmed = "confirmed",
	ongoing = "ongoing",
	history = "history",
}

export interface FetchOrdersRequestPayload extends PaginationPayload {
	status: OrderStatus | string;
}

export interface GetTotalActiveOrderValueResponsePayload {
	activeOrdersAmount: number;
}
